import { Button, Chip, IconButton, TextField } from "@mui/material";
import { CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox } from "@mui/icons-material";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { Skeleton } from "@mui/material";
import { IFilterParams } from "ag-grid-community";
import { IFilterReactComp } from "ag-grid-react";
import styles from "components/portfolio/customerFilters/CheckboxFilter.module.scss";
import { debounce } from "lodash";
import { ChangeEvent, Component } from "react";
import CheckboxTree from "react-checkbox-tree";
import { LoadingStatus } from "types/app";

interface CheckboxFilterParams {
  keyword: string;
  loadingStatus: LoadingStatus;
  data: CheckboxItem[];
  initialData: CheckboxItem[];
  filterActive: boolean;
  expanded: string[];
  checked: string[];
}

interface CheckboxItem {
  label: string;
  value: string;

  children?: CheckboxItem[];
}

class WorkloadFilter
  extends Component<IFilterParams, CheckboxFilterParams>
  implements IFilterReactComp
{
  private title: string;
  constructor(props: IFilterParams) {
    super(props);
    this.state = {
      keyword: "",
      data: [],
      initialData: [],
      filterActive: false,
      expanded: [],
      checked: [],
      loadingStatus: "notLoaded",
    };

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onTextValueChange = this.onTextValueChange.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleCheck = this.handleCheck.bind(this);
    this.handleClearKeyWord = this.handleClearKeyWord.bind(this);
    this.handleExpand = this.handleExpand.bind(this);
    this.title = this.props.colDef.filterParams?.title ?? props.colDef.headerName;
  }

  componentDidMount() {
    const { nullOptionName, workloadCategory, workloadStage } = this.props.colDef.filterParams;

    const data: CheckboxItem[] = workloadCategory.map((c: any) => ({
      label: c.value,
      value: c.value,
      children: [
        ...workloadStage.map((s: any) => ({
          label: s.value,
          value: `${c.value} - ${s.value}`,
        })),
      ],
    }));

    if (nullOptionName) {
      data.push({
        label: nullOptionName,
        value: "null",
      });
    }
    this.setState({
      data,
      initialData: data,
      loadingStatus: "loaded",
    });
  }

  // Return true if the filter is active. If active then 1) the grid will show the filter icon in the column
  // header and 2) the filter will be included in the filtering of the data.
  isFilterActive() {
    return this.state.filterActive;
  }

  doesFilterPass() {
    return true;
  }

  getModel() {
    const { filterActive, checked } = this.state;
    if (filterActive && checked.length) {
      return {
        filterType: "checkboxFilter",
        type: "in",
        filter: checked.join("|"),
      };
    } else {
      return null;
    }
  }

  setModel(model: any) {
    if (!!model) {
      const { filter } = model;
      if (filter) {
        this.setState(
          {
            checked: filter.split("|"),
            filterActive: true,
          },
          () => {
            this.props.filterChangedCallback();
          }
        );
      }
    } else {
      this.setState(
        {
          filterActive: false,
          keyword: "",
          checked: [],
        },
        () => {
          this.props.filterChangedCallback();
        }
      );
    }
  }

  onButtonClick(type: "clear" | "apply") {
    switch (type) {
      case "clear":
        return () => {
          this.setState(
            {
              keyword: "",
              data: this.state.initialData,
              checked: [],
              filterActive: false,
            },
            () => {
              this.props.filterChangedCallback();
            }
          );
        };
      case "apply":
        return () => {
          this.setState({ filterActive: true }, () => {
            this.props.filterChangedCallback();
          });
        };
    }
  }

  onTextValueChange(e: ChangeEvent<HTMLInputElement>) {
    const currentValue = e.target.value;
    this.setState(
      {
        keyword: currentValue,
      },
      debounce(this.filterTree, 1000)
    );
  }

  filterTree() {
    this.setState({ loadingStatus: "notLoaded" });
    const { keyword, initialData, expanded } = this.state;
    // Reset nodes back to unfiltered state
    if (!keyword) {
      this.setState({
        data: initialData,
        loadingStatus: "loaded",
      });
      return;
    }

    const data = initialData.reduce(this.filterNodes, []);
    // open nodes that has children matched
    const expand = data.reduce(this.expandNodes, expanded);

    this.setState({ data, expanded: expand, loadingStatus: "loaded" });
  }

  filterNodes = (filtered: CheckboxItem[], node: CheckboxItem) => {
    const { keyword } = this.state;
    const children = (node.children || []).reduce(this.filterNodes, []);

    if (
      // Node's label matches the search string
      node.label.toLocaleLowerCase().indexOf(keyword.toLocaleLowerCase()) > -1 ||
      node.value.toLocaleLowerCase().indexOf(keyword.toLocaleLowerCase()) > -1 ||
      // Or a children has a matching node
      children.length
    ) {
      filtered.push({
        ...node,
        children: children.length ? children : undefined,
      });
    }

    return filtered;
  };

  expandNodes = (filtered: string[], node: CheckboxItem) => {
    const children = (node.children || []).reduce(this.expandNodes, []);

    if (children.length) {
      filtered.push(...children);
    }
    if (node.children) {
      filtered.push(node.value);
    }

    return filtered;
  };

  handleDelete = (value: string) => () => {
    this.setState((pre) => ({
      checked: pre.checked.filter((x) => x !== value),
    }));
  };

  handleCheck = (checked: string[]) => {
    this.setState({
      checked,
    });
  };

  handleExpand = (expanded: string[]) => {
    this.setState({
      expanded,
    });
  };

  handleClearKeyWord = () => {
    this.setState({
      keyword: "",
      data: this.state.initialData,
    });
  };

  chipLabelTransform = (label: string, data?: any) => {
    if (data?.length) return label;
  };

  render() {
    const { data, expanded, checked, keyword, loadingStatus, filterActive } = this.state;
    const { queryParams } = this.props.colDef.filterParams;
    if (loadingStatus === "failed")
      return (
        <div className={styles.failLoad}>
          <span>Sorry, fail to load data</span>
        </div>
      );
    return (
      <div>
        <p className={styles.filterTitle}>{this.title}</p>
        <div className={styles.filterWrapper}>
          <div>
            {checked.slice(0, 10).map((value) => (
              <Chip
                label={this.chipLabelTransform(value, data)}
                onDelete={this.handleDelete(value)}
                key={value}
                size="small"
                color="primary"
                classes={{
                  root: styles.chipRoot,
                  label: styles.chipLabel,
                  colorPrimary: styles.chipColorPrimary,
                }}
              />
            ))}
            {checked.length > 10 ? <div>{`${checked.length - 10} more`}</div> : null}
          </div>
          <TextField
            fullWidth={true}
            placeholder="Enter Search Criteria..."
            variant="standard"
            margin="dense"
            autoFocus={true}
            value={keyword}
            onChange={this.onTextValueChange}
            InputProps={{
              endAdornment: keyword ? (
                <IconButton
                  onClick={this.handleClearKeyWord}
                  size="small"
                >
                  <HighlightOffOutlinedIcon
                    color="primary"
                    fontSize="small"
                  />
                </IconButton>
              ) : null,
            }}
          />
          <div>
            {loadingStatus === "loaded" ? (
              <CheckboxTree
                nodes={data}
                checked={checked}
                expanded={expanded}
                icons={{
                  check: (
                    <CheckBox
                      className={styles.primaryColor}
                      style={{ height: 25, width: 25 }}
                    />
                  ),
                  uncheck: (
                    <CheckBoxOutlineBlank
                      className={styles.primaryColor}
                      style={{ height: 25, width: 25 }}
                    />
                  ),
                  expandOpen: <span className="ag-icon ag-icon-tree-open" />,
                  expandClose: <span className="ag-icon ag-icon-tree-closed" />,
                  halfCheck: (
                    <IndeterminateCheckBox
                      className={styles.primaryColor}
                      style={{ height: 25, width: 25 }}
                    />
                  ),
                }}
                showNodeTitles={true}
                showNodeIcon={false}
                onCheck={this.handleCheck}
                onExpand={this.handleExpand}
                id={queryParams}
              />
            ) : (
              <>
                {Array.from({ length: 4 }).map((x, i) => (
                  <Skeleton
                    key={`${x}${i}`}
                    width={160}
                    height={28}
                    variant="rectangular"
                    style={{ margin: "6px 12px" }}
                  />
                ))}
              </>
            )}
          </div>
        </div>
        <div className="ag-filter-apply-panel">
          <Button
            variant="text"
            disabled={!filterActive}
            onClick={this.onButtonClick("clear")}
            className="ag-standard-button ag-filter-apply-panel-button"
          >
            Reset filter
          </Button>
          <Button
            disabled={checked.length > 0 ? false : true}
            variant="text"
            onClick={this.onButtonClick("apply")}
            className="ag-standard-button ag-filter-apply-panel-button"
          >
            Apply filter
          </Button>
        </div>
      </div>
    );
  }
}

export default WorkloadFilter;
